import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Plus, CheckCircle } from 'lucide-react';
import toast from '../../../utils/toast';
import {
  plantationService,
  PlantationPointageRegimes,
  PlantationCampagne,
  PlantationParcelle,
} from '../../../services/plantation.service';

interface PointageRegimesTabProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  parcelles: PlantationParcelle[];
  campagneActive?: string;
}

const EMPTY_FORM = {
  date: new Date().toISOString().slice(0, 10),
  parcelleId: '',
  campagneId: '',
  matriculeAAPI: '',
  matriculeRecolteur: '',
  nombreBons: 0,
  nombreVerts: 0,
  nombrePourris: 0,
  nombreAnormaux: 0,
  nombreEgrappes: 0,
};

export default function PointageRegimesTab({
  entiteId,
  campagnes,
  parcelles,
  campagneActive,
}: PointageRegimesTabProps) {
  const [pointages, setPointages] = useState<PlantationPointageRegimes[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState<string | null>(null);

  // Filtres
  const [filterDate, setFilterDate] = useState('');
  const [filterParcelle, setFilterParcelle] = useState('');
  const [filterCampagne, setFilterCampagne] = useState(campagneActive || '');

  // Formulaire modal
  const [form, setForm] = useState({ ...EMPTY_FORM, campagneId: campagneActive || '' });

  const charger = async () => {
    setLoading(true);
    try {
      const data = await plantationService.listPointageRegimes(entiteId, {
        date: filterDate || undefined,
        parcelleId: filterParcelle || undefined,
        campagneId: filterCampagne || undefined,
      });
      setPointages(data);
    } catch {
      toast.error('Erreur chargement pointages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, filterParcelle, filterCampagne]);

  // KPIs
  const kpis = useMemo(() => {
    const total = pointages.reduce((s, p) => s + p.nombreTotal, 0);
    const bons = pointages.reduce((s, p) => s + p.nombreBons, 0);
    const vertsEtPourris = pointages.reduce((s, p) => s + p.nombreVerts + p.nombrePourris, 0);
    const anormauxEtEgrappes = pointages.reduce((s, p) => s + p.nombreAnormaux + p.nombreEgrappes, 0);
    return { total, bons, vertsEtPourris, anormauxEtEgrappes };
  }, [pointages]);

  // Données graphique groupées par date
  const chartData = useMemo(() => {
    const map: Record<string, {
      date: string;
      bons: number;
      verts: number;
      pourris: number;
      anormaux: number;
      egrappes: number;
    }> = {};
    for (const p of pointages) {
      const d = p.date.slice(0, 10);
      if (!map[d]) map[d] = { date: d, bons: 0, verts: 0, pourris: 0, anormaux: 0, egrappes: 0 };
      map[d].bons += p.nombreBons;
      map[d].verts += p.nombreVerts;
      map[d].pourris += p.nombrePourris;
      map[d].anormaux += p.nombreAnormaux;
      map[d].egrappes += p.nombreEgrappes;
    }
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [pointages]);

  const nombreTotal = useMemo(
    () => form.nombreBons + form.nombreVerts + form.nombrePourris + form.nombreAnormaux + form.nombreEgrappes,
    [form],
  );

  const handleSave = async () => {
    if (!form.date) {
      toast.error('La date est obligatoire');
      return;
    }
    setSaving(true);
    try {
      await plantationService.createPointageRegimes({
        entiteId,
        date: form.date,
        parcelleId: form.parcelleId || undefined,
        campagneId: form.campagneId || undefined,
        matriculeAAPI: form.matriculeAAPI || undefined,
        matriculeRecolteur: form.matriculeRecolteur || undefined,
        nombreTotal,
        nombreBons: form.nombreBons,
        nombreVerts: form.nombreVerts,
        nombrePourris: form.nombrePourris,
        nombreAnormaux: form.nombreAnormaux,
        nombreEgrappes: form.nombreEgrappes,
        statut: 'brouillon',
      });
      toast.success('Pointage enregistré');
      setShowModal(false);
      setForm({ ...EMPTY_FORM, campagneId: campagneActive || '' });
      charger();
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleValider = async (id: string) => {
    setValidating(id);
    try {
      await plantationService.validerPointage(id);
      toast.success('Pointage validé');
      charger();
    } catch {
      toast.error('Erreur validation');
    } finally {
      setValidating(null);
    }
  };

  const parcelleNom = (id?: string) => {
    if (!id) return '—';
    const p = parcelles.find((x) => x.id === id);
    return p ? p.nom : id;
  };

  const campagneNom = (id?: string) => {
    if (!id) return '—';
    const c = campagnes.find((x) => x.id === id);
    return c ? c.libelle : id;
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date</label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Parcelle</label>
          <select
            value={filterParcelle}
            onChange={(e) => setFilterParcelle(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Toutes</option>
            {parcelles.map((p) => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Campagne</label>
          <select
            value={filterCampagne}
            onChange={(e) => setFilterCampagne(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Toutes</option>
            {campagnes.map((c) => (
              <option key={c.id} value={c.id}>{c.libelle}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="ml-auto flex items-center gap-2 bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-teal-800"
        >
          <Plus size={15} /> Saisir pointage
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-600 mb-1">Total régimes</div>
          <div className="text-2xl font-bold text-blue-700">{kpis.total.toLocaleString('fr-FR')}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-xs text-green-600 mb-1">Bons</div>
          <div className="text-2xl font-bold text-green-700">{kpis.bons.toLocaleString('fr-FR')}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs text-amber-600 mb-1">Verts + Pourris</div>
          <div className="text-2xl font-bold text-amber-700">{kpis.vertsEtPourris.toLocaleString('fr-FR')}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-xs text-red-600 mb-1">Anormaux + Égrappés</div>
          <div className="text-2xl font-bold text-red-700">{kpis.anormauxEtEgrappes.toLocaleString('fr-FR')}</div>
        </div>
      </div>

      {/* Graphique */}
      {chartData.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700 mb-3">Répartition par date</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11, marginTop: 8 }} />
              <Bar dataKey="bons" name="Bons" stackId="a" fill="#22c55e" />
              <Bar dataKey="verts" name="Verts" stackId="a" fill="#eab308" />
              <Bar dataKey="pourris" name="Pourris" stackId="a" fill="#ef4444" />
              <Bar dataKey="anormaux" name="Anormaux" stackId="a" fill="#f97316" />
              <Bar dataKey="egrappes" name="Égrappés" stackId="a" fill="#94a3b8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Chargement...</div>
        ) : pointages.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Aucun pointage enregistré</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Date', 'AAPI', 'Récolteur', 'Parcelle', 'Total', 'Bons', 'Verts', 'Pourris', 'Anormaux', 'Égrappés', 'Statut', 'Action'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pointages.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(p.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-3 py-2">{p.matriculeAAPI || '—'}</td>
                    <td className="px-3 py-2">{p.matriculeRecolteur || '—'}</td>
                    <td className="px-3 py-2">{parcelleNom(p.parcelleId)}</td>
                    <td className="px-3 py-2 font-medium">{p.nombreTotal}</td>
                    <td className="px-3 py-2 text-green-700">{p.nombreBons}</td>
                    <td className="px-3 py-2 text-yellow-600">{p.nombreVerts}</td>
                    <td className="px-3 py-2 text-red-600">{p.nombrePourris}</td>
                    <td className="px-3 py-2 text-orange-600">{p.nombreAnormaux}</td>
                    <td className="px-3 py-2 text-slate-500">{p.nombreEgrappes}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${
                        p.statut === 'valide'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {p.statut === 'valide' ? 'Validé' : 'Brouillon'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {p.statut === 'brouillon' && (
                        <button
                          onClick={() => handleValider(p.id)}
                          disabled={validating === p.id}
                          className="flex items-center gap-1 text-teal-700 hover:text-teal-900 disabled:opacity-50"
                        >
                          <CheckCircle size={13} />
                          {validating === p.id ? '...' : 'Valider'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal saisie */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-800">Saisir un pointage régimes</h2>
            </div>
            <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parcelle</label>
                  <select
                    value={form.parcelleId}
                    onChange={(e) => setForm({ ...form, parcelleId: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">— Sélectionner —</option>
                    {parcelles.map((p) => (
                      <option key={p.id} value={p.id}>{p.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Campagne</label>
                  <select
                    value={form.campagneId}
                    onChange={(e) => setForm({ ...form, campagneId: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">— Sélectionner —</option>
                    {campagnes.map((c) => (
                      <option key={c.id} value={c.id}>{c.libelle}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Matricule AAPI</label>
                  <input
                    type="text"
                    value={form.matriculeAAPI}
                    onChange={(e) => setForm({ ...form, matriculeAAPI: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Ex : AAPI-001"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Matricule Récolteur</label>
                  <input
                    type="text"
                    value={form.matriculeRecolteur}
                    onChange={(e) => setForm({ ...form, matriculeRecolteur: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Ex : REC-042"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Comptage régimes</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {([
                    { key: 'nombreBons', label: 'Bons', color: 'green' },
                    { key: 'nombreVerts', label: 'Verts', color: 'yellow' },
                    { key: 'nombrePourris', label: 'Pourris', color: 'red' },
                    { key: 'nombreAnormaux', label: 'Anormaux', color: 'orange' },
                    { key: 'nombreEgrappes', label: 'Égrappés', color: 'slate' },
                  ] as { key: keyof typeof form; label: string; color: string }[]).map(({ key, label, color }) => (
                    <div key={key}>
                      <label className={`block text-xs font-medium text-${color}-600 mb-1`}>{label}</label>
                      <input
                        type="number"
                        min={0}
                        value={(form[key] as number) || 0}
                        onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value) || 0 })}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium text-blue-700 mb-1">Total (auto)</label>
                    <div className="w-full border border-blue-200 bg-blue-50 rounded-lg px-3 py-2 text-sm font-bold text-blue-800">
                      {nombreTotal}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={() => { setShowModal(false); setForm({ ...EMPTY_FORM, campagneId: campagneActive || '' }); }}
                className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
